import { LevelCatalog, Modes, freeRoutes } from "@highmarch/app";
import { rowHtml, sheetHeadHtml, wellHtml } from "../components";
import { evBannerHtml, evChipHtml, evKickHtml } from "../hub/evKit";
import { SHOP_SKIN } from "../shopSkin";
import "../styles/evTracks.css";
import { fmtCompact } from "../kit";
import { icon } from "../iconsKit";
import { SIGIL_RARITIES, essenceIconHtml, sigilIconHtml } from "../forgeArt";
import { RARITY_LABEL } from "../gearUi";
import { paintedIconHtml } from "../featureIcons";
import { infinityGlyph, ironStarGlyph } from "../modeGlyphs";
import { bindMilestoneClaims, milestoneTrackHtml } from "./milestoneTrack";

/** What the Free rewards sheet needs. */
export interface FreeRewardsCtx {
  readonly catalog: LevelCatalog;
  readonly modes: Modes;
  readonly now: () => number;
  /** Saves and redraws the hub (the More badge). */
  readonly onChange: () => void;
  /** Opens More → Endless (its PLAY). */
  readonly openEndless?: () => void;
}

/** The purse: Forge Essence and the sigils held, by rarity, each on its rarity square. */
export function forgePurseHtml(catalog: LevelCatalog): string {
  const f = catalog.profile.forge;
  const sigils = SIGIL_RARITIES.map((r) => `<span class="ps r${r}${(f.sigils[r] ?? 0) === 0 ? " none" : ""}" title="${RARITY_LABEL[r]} sigils">${wellHtml({ art: sigilIconHtml(r, 26), rarity: r, size: "sm", count: fmtCompact(f.sigils[r] ?? 0), tag: "span" })}</span>`).join("");
  return `<div class="frpurse"><span class="pe">${essenceIconHtml(34)}<b class="disp">${fmtCompact(f.essence)}</b><small>Essence</small></span><span class="sigs">${sigils}</span></div>`;
}

function bodyHtml(ctx: FreeRewardsCtx): string {
  const now = ctx.now(), m = ctx.modes, routes = freeRoutes(ctx.catalog, now);
  const endless = routes.rows.filter((r) => r.source === "endless"), iron = routes.rows.filter((r) => r.source === "iron");
  const s = ctx.catalog.modes;
  const endlessHtml = m.endlessOpen
    ? `${evKickHtml(`${infinityGlyph(18)} ENDLESS`, s.weekWaves > 0 ? `wave ${s.weekWaves}` : "this week")}${milestoneTrackHtml(endless, "endless", s.week)}
       ${ctx.openEndless ? `<button type="button" class="frlink endlessgo">${infinityGlyph(18)}<span>Play Endless</span>${icon("chev", 14)}</button>` : ""}`
    : `${evKickHtml(`${infinityGlyph(18)} ENDLESS`)}<p class="frlock">${icon("lock", 14)} Level ${m.endlessUnlockAfter}</p>`;
  const ironHtml = iron.length > 0
    ? `${evKickHtml(`${ironStarGlyph(18)} IRON STARS`, `${s.ironStarCount} so far`)}${milestoneTrackHtml(iron, "iron", -1)}`
    : `${evKickHtml(`${ironStarGlyph(18)} IRON STARS`)}<p class="frlock">${icon("lock", 14)} Three-star a level</p>`;
  const more = [
    rowHtml({ lead: paintedIconHtml("eventEmber", 30), title: "Events", sub: "Track rewards", compact: true }),
    rowHtml({ lead: paintedIconHtml("achievementGold", 30), title: "Achievements", sub: "Forge goals", compact: true }),
    rowHtml({ lead: paintedIconHtml("crate", 30), title: "Daily Orders", sub: "Weekly chest", compact: true }),
    rowHtml({ lead: essenceIconHtml(30), title: "Battles", sub: "Every chest", compact: true }),
  ].join("");
  return `${evBannerHtml({ scene: "evfree", title: "Free rewards", chips: routes.claimable > 0 ? evChipHtml(`<b>${routes.claimable}</b> to claim`) : "" })}
    ${forgePurseHtml(ctx.catalog)}
    ${endlessHtml}${ironHtml}
    ${evKickHtml("MORE ROUTES")}<div class="frmore">${more}</div>`;
}

/**
 * More → Free rewards (gear v2, docs/GEAR_V2.md §4): one place for the free routes to gear power — the forge purse
 * (essence and sigils held), this week's Endless milestones and the Iron star totals with CLAIM, and where else the
 * forge's goods come from. Its red count (the More row, and so the More tab) is `freeRoutes().claimable`.
 */
export function showFreeRewardsSheet(parent: HTMLElement, ctx: FreeRewardsCtx): HTMLElement {
  parent.querySelector(":scope > .sheet.freerewards")?.remove();
  const sheet = document.createElement("div");
  sheet.className = `sheet panel freerewards ${SHOP_SKIN}`;
  parent.append(sheet);
  const render = (): void => {
    const keep = sheet.querySelector<HTMLElement>(".frbody")?.scrollTop ?? 0;
    sheet.innerHTML = `${sheetHeadHtml({ title: "Free rewards", portrait: essenceIconHtml(46), tint: "ember" })}<div class="frbody">${bodyHtml(ctx)}</div>`;
    const body = sheet.querySelector<HTMLElement>(".frbody");
    if (body) body.scrollTop = keep;
    sheet.querySelector<HTMLButtonElement>(".close")!.onclick = () => sheet.remove();
    sheet.querySelector<HTMLButtonElement>(".endlessgo")?.addEventListener("click", () => { sheet.remove(); ctx.openEndless?.(); });
    bindMilestoneClaims(sheet, { balance: ctx.catalog.balance, layerHost: sheet, toastHost: parent }, (kind, week, index) => {
      const c = kind === "iron" ? ctx.modes.claimIronMilestone(index, ctx.now()) : ctx.modes.claimEndlessMilestone(week, index, ctx.now());
      if (typeof c !== "string") ctx.onChange();
      return c;
    }, () => { if (sheet.isConnected) render(); });
  };
  render();
  return sheet;
}
